// Generated by GitHub Copilot
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type AuthenticatorAppEnrollment from '#core/entities/auth/mfa/AuthenticatorAppEnrollment.ts'
import { useUserState } from '#core/state/UserState.ts'
import { QrCode } from '#components/primitives/components/qr-code.tsx'
import type { AuthenticatorAppState } from './authenticator-app-state.ts'
import { AuthenticatorAppNotice } from './authenticator-app-notice.tsx'
import { AuthenticatorSecret } from './authenticator-secret.tsx'

const CODE_LENGTH = 6
const CODE_INPUT_ID = 'mfaSetupCodeInput'

/** Setup step: the pending app's QR code and secret, then the first code that confirms it. */
export function AuthenticatorAppSetup({ state, enrollment }: { state: AuthenticatorAppState; enrollment: AuthenticatorAppEnrollment }) {
  const { t } = useTranslation(['settings', 'auth'])
  const userState = useUserState()
  const formRef = useRef<HTMLFormElement | null>(null)

  const submit = () => {
    state.validated = true
    if (formRef.current && !formRef.current.checkValidity()) return
    void state.confirm(userState)
  }

  return (
    <div className="border-top mt-3 pt-3">
      <div className="row g-4">
        <div className="col-12 col-sm-auto d-flex justify-content-center">
          <QrCode value={enrollment.otpauth_uri} size={176} enlargeable className="bg-white p-2 rounded border" />
        </div>
        <div className="col">
          <p className="mb-2">{t('settings:security.authenticator.setup.scan')}</p>
          <p className="text-secondary small mb-1">{t('settings:security.authenticator.setup.manual')}</p>
          <AuthenticatorSecret enrollment={enrollment} />

          <form
            ref={formRef}
            className={state.validated ? 'was-validated' : undefined}
            noValidate
            onSubmit={(e) => {
              e.preventDefault()
              submit()
            }}
          >
            <p className="mb-2">{t('settings:security.authenticator.setup.confirmIntro')}</p>
            <div className="mb-3" style={{ maxWidth: '14rem' }}>
              <label htmlFor={CODE_INPUT_ID} className="form-label">{t('auth:mfa.codeLabel')}</label>
              <input
                type="text"
                id={CODE_INPUT_ID}
                className="form-control text-center"
                value={state.code}
                onChange={(e) => {
                  // Codes arrive pasted as "123 456": the length is capped after stripping, so the paste is never cut short.
                  state.code = e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH)
                }}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                minLength={CODE_LENGTH}
                required
                autoFocus
              />
              <div className="invalid-feedback">{t('auth:mfa.codeValidation')}</div>
            </div>

            <AuthenticatorAppNotice notice={state.notice} />

            <div className="d-flex flex-wrap gap-2">
              <button type="submit" className="btn btn-primary d-inline-flex align-items-center gap-2" disabled={state.busy}>
                {state.busy ? t('auth:mfa.verifying') : t('auth:mfa.verify')}
                {state.busy && <span className="spinner-border spinner-border-sm" aria-hidden="true" />}
              </button>
              <button type="button" className="btn btn-outline-secondary" disabled={state.busy} onClick={() => state.cancelSetup()}>
                {t('shared.actions.cancel')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
